import { Link } from '../../../../../../components/Toolkit/Button/Link';
import styled, { useTheme } from 'styled-components';
import { Star } from '../../../../../../components/PageWrappers/AdDetails/components/GoogleReviews/Star/Star';

export interface StarRatingGroupProps {
  rating: number;
  numberOfStars?: number;
  totalReviews?: string;
  showRatingNumber?: boolean;
  starWidth?: string;
  starHeight?: string;
  reviewText?: string;
  clickTargetElementId?: string;
  totalReviewsTrackingId?: string;
  multipleStars?: boolean;
}

export const RatingGroup = styled.div`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARK};
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing.S12};
`;

const TotalReviews = styled.span``;

const Rating = styled.span`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const MaxRating = styled.span`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARK};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const StarGroup = styled.div`
  line-height: 0;

  svg:not(:last-child) {
    margin-right: ${({ theme }) => theme.spacing.S4};
  }
`;

const SLink = styled(Link)`
  color: ${({ theme }) => theme.colors.BLUE};
  margin-left: ${({ theme }) => theme.spacing.S12};
`;

function handleClick(clickTargetElementId: string) {
  const element = document.getElementById(`${clickTargetElementId}`);
  element && element.scrollIntoView({ behavior: 'smooth' });
}

function StarRatingGroup({
  rating,
  numberOfStars = 5,
  totalReviews,
  showRatingNumber = true,
  starWidth = '24',
  starHeight = '24',
  reviewText = 'reviews',
  clickTargetElementId = 'google-dealer-reviews',
  totalReviewsTrackingId = 'google-reviews-total-reviews',
  multipleStars = true,
}: StarRatingGroupProps) {
  const theme = useTheme();

  const halfStar = !Number.isInteger(rating);
  const fullStars = Math.floor(rating);

  const stars = Array(numberOfStars)
    .fill('EMPTY')
    .map((star, index) => {
      if (index < fullStars) {
        star = 'FULL';
      } else if (halfStar && index === fullStars) {
        star = 'HALF';
      }

      return star;
    });

  return (
    <>
      <StarGroup data-testid="google-reviews-stars">
        {multipleStars ? (
          stars.map((star, index) => (
            <Star
              key={index}
              starType={star}
              fillColor={theme.colors.ORANGE}
              emptyColor={theme.colors.GREY_LIGHT}
              width={starWidth}
              height={starHeight}
            />
          ))
        ) : (
          <Star
            starType={'FULL'}
            fillColor={theme.colors.ORANGE}
            emptyColor={theme.colors.GREY_LIGHT}
            width={starWidth}
            height={starHeight}
          />
        )}
      </StarGroup>
      {showRatingNumber && (
        <>
          <RatingGroup>
            <Rating>{rating}</Rating>
            <MaxRating>/{numberOfStars}</MaxRating>
          </RatingGroup>

          <TotalReviews>
            {totalReviews && (
              <SLink
                ofType="SECONDARY"
                onClick={() => handleClick(clickTargetElementId)}
                data-tracking={totalReviewsTrackingId}
                fontSize="M14"
              >
                {totalReviews} {reviewText}
              </SLink>
            )}
          </TotalReviews>
        </>
      )}
    </>
  );
}

export { StarRatingGroup };
